import {
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { UseFormReturn } from "react-hook-form";
import { AdminMeetingTypeTag } from "design-system";
import { MeetingTypeInput } from "design-system/components/AdminMeetingTypeTag";
import DashedButton from "components/Button/DashedButton";
import TemplateMeetingTypeForm from "./Form/TemplateMeetingTypeForm";

function MeetingTypeDisplay({ meetingType, includeLabel }: { meetingType: MeetingTypeInput | null | undefined; includeLabel?: boolean }) {
  if (!meetingType) {
    return (
      <Tooltip label="Click to Choose a Meeting Type" placement="top">
        <Box display="inline-block">
          <DashedButton title="Choose a Meeting Type" />
        </Box>
      </Tooltip>
    );
  }
  if (includeLabel) {
    return <AdminMeetingTypeTag.Badge meetingType={meetingType} />;
  }
  return <AdminMeetingTypeTag.IconBadge meetingType={meetingType} />;
}

interface MeetingTypeSelectorProps {
  form: UseFormReturn<any>;
  disabled?: boolean;
  includeLabel?: boolean;
  autoFocus?: boolean;
  /** Forwarded to TemplateMeetingTypeForm. When provided, picking "Recurring"
   *  calls this callback (intended to open the Google Calendar import modal)
   *  instead of setting `meetingType` on the form. */
  onRecurringSelected?: () => void;
}

export default function MeetingTypeSelector({
  form,
  disabled = false,
  includeLabel,
  autoFocus = false,
  onRecurringSelected,
}: MeetingTypeSelectorProps) {
  const { watch } = form;
  const { isOpen, onOpen, onClose, getDisclosureProps } = useDisclosure({ defaultIsOpen: autoFocus });
  const disclosureProps = getDisclosureProps();

  const meetingType = watch("meetingType");

  if (disabled) {
    return (
      <Box>
        <MeetingTypeDisplay meetingType={meetingType} includeLabel={includeLabel} />
      </Box>
    );
  }

  return (
    <Popover
      {...disclosureProps}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      closeOnBlur={!!meetingType}
      closeOnEsc={!!meetingType}
    >
      <PopoverTrigger>
        <Box cursor="pointer" _hover={{ opacity: 0.8 }}>
          <MeetingTypeDisplay meetingType={meetingType} includeLabel={includeLabel} />
        </Box>
      </PopoverTrigger>
      <PopoverContent
        pt="3"
        bg="var(--chakra-colors-chakra-body-bg)"
        minW={{ base: "100%", lg: "max-content" }}
        width="100%"
        display={isOpen ? "block" : "none"}
      >
        <PopoverBody>
          <TemplateMeetingTypeForm
            form={form}
            onChangeCallback={onClose}
            onRecurringSelected={onRecurringSelected ? () => { onClose(); onRecurringSelected(); } : undefined}
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
